import { Component, Input, Output, EventEmitter, OnInit, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ProductService } from 'src/app/services/product.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { finalize, filter } from 'rxjs/operators';

@Component({
  selector: 'app-meta-app',
  templateUrl: './meta-app.component.html',
  styleUrls: ['./meta-app.component.scss'],
  providers: [MessageService]
})
export class MetaAppComponent implements OnInit {

  isMobile = false;

  ngOnInit() {
    this.isMobile = window.innerWidth <= 768;
  }

  @Input() instagramAccounts: any[] = [];
  @Input() visible: boolean = false;
  @Input() loading: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() addInstagramAccount = new EventEmitter<void>();
  @Output() removeInstagramAccountEvent = new EventEmitter<any>();
  @Output() close = new EventEmitter<void>();

  // Segunda sidebar: configurações da conta FB selecionada
  showIgSettings = false;
  selectedFbAccount: any = null;
  igPages: any[] = [];
  loadingIgPages = false;
  savingSettings = false;
  expandedPageIds = new Set<number>();
  private originalPages: any[] = [];

  constructor(
    private productService: ProductService,
    private errorHandler: ErrorHandlerService,
    private messageService: MessageService,
    private injector: Injector
  ) {}

  onAddInstagramAccount(): void {
    localStorage.removeItem('cachedInstagramAccounts');
    this.addInstagramAccount.emit();
  }

  onRemoveInstagramAccount(account: any): void {
    localStorage.removeItem('cachedInstagramAccounts');
    this.removeInstagramAccountEvent.emit(account);
  }

  openIgSettings(acct: any): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.selectedFbAccount = acct;
    this.igPages = [];
    this.expandedPageIds.clear();
    this.loadingIgPages = true;
    this.showIgSettings = true;

    // Cache-first com ETag.
    const igPagesResource = this.productService.getIgAccountsForFacebookUserRevalidated(acct.uid);

    toObservable(igPagesResource.data, { injector: this.injector }).pipe(
      filter((res): res is { pages: any[] } => res !== null)
    ).subscribe(res => {
        this.igPages = res.pages ?? [];
        this.originalPages = this.igPages.map(p => ({ ...p }));
        this.loadingIgPages = false;
    });

    toObservable(igPagesResource.error, { injector: this.injector }).pipe(
      filter((err): err is any => err !== null)
    ).subscribe(err => {
        this.loadingIgPages = false;
        this.errorHandler.handle(err, 'Erro ao carregar contas do Instagram.');
    });
  }

  closeIgSettings(): void {
    this.showIgSettings = false;
    this.selectedFbAccount = null;
    this.igPages = [];
  }

  togglePageExpanded(pageId: number): void {
    if (this.expandedPageIds.has(pageId)) {
      this.expandedPageIds.delete(pageId);
    } else {
      this.expandedPageIds.add(pageId);
    }
  }

  openInstagramProfile(igUsername: string | undefined, event: MouseEvent): void {
    event.stopPropagation();
    if (!igUsername) return;
    window.open(`https://www.instagram.com/${igUsername}`, '_blank', 'noopener');
  }

  isPageExpanded(pageId: number): boolean {
    return this.expandedPageIds.has(pageId);
  }

  onToggleMentionsEnabled(page: any): void {
    // Apenas atualiza estado local — salvo ao clicar em "Salvar configurações"
    if (!page.mentions_enabled) {
      page.mentions_auto_publish = false;
    }
  }

  onToggleAutoPublish(page: any): void {
    // Apenas atualiza estado local
  }

  get hasUnsavedChanges(): boolean {
    return this.igPages.some(page => {
      const original = this.originalPages.find(o => o.id === page.id);
      return !original ||
        original.mentions_enabled !== page.mentions_enabled ||
        original.mentions_auto_publish !== page.mentions_auto_publish;
    });
  }

  saveAllSettings(): void {
    const modified = this.igPages.filter(page => {
      const original = this.originalPages.find(o => o.id === page.id);
      return !original ||
        original.mentions_enabled !== page.mentions_enabled ||
        original.mentions_auto_publish !== page.mentions_auto_publish;
    });

    if (!modified.length) return;

    this.savingSettings = true;

    const requests = modified.map(page =>
      this.productService.updateMentionsSettings(page.id, page.mentions_enabled, page.mentions_auto_publish)
    );

    forkJoin(requests).pipe(
      finalize(() => this.savingSettings = false)
    ).subscribe({
      next: () => {
        this.originalPages = this.igPages.map(p => ({ ...p }));
        this.messageService.add({ severity: 'success', summary: 'Salvo', detail: 'Configurações salvas com sucesso.' });
      },
      error: (err) => {
        this.errorHandler.handle(err, 'Erro ao salvar configurações.');
      }
    });
  }
}
